/*
 * Curated routine: review the selected products, then add them to the bag in one step.
 * Nothing is added until the customer presses the add button in the review.
 * Products already included in a selected set are listed as covered and not added twice.
 */
if (!customElements.get('routine-curated')) {
  class RoutineCurated extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.routine-curated__form');
      this.reviewButton = this.querySelector('.routine-curated__review-button');
      this.review = this.querySelector('.routine-curated__review');
      this.reviewList = this.querySelector('.routine-curated__review-list');
      this.totalValue = this.querySelector('.routine-curated__total-value');
      this.addButton = this.querySelector('.routine-curated__add-button');
      this.message = this.querySelector('.routine-curated__message');
      this.busy = false;

      this.reviewButton.addEventListener('click', () => this.openReview());
      this.addButton.addEventListener('click', () => this.addToBag());
      this.form.addEventListener('change', () => {
        this.markCovered();
        if (!this.review.hidden) this.renderReview();
      });
      this.markCovered();
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    getSelection() {
      const items = [];
      this.form.querySelectorAll('.routine-curated__item').forEach((item) => {
        const input = item.querySelector('input[type="checkbox"], input[type="radio"]');
        if (!input || !input.checked || input.disabled || !input.value) return;
        const select = item.querySelector('[data-variant-select]');
        const fixed = item.querySelector('[data-variant-fixed]');
        let variantId = null;
        let variantTitle = '';
        let price = 0;
        if (select) {
          const option = select.selectedOptions[0];
          if (option && option.value) {
            variantId = Number(option.value);
            variantTitle = option.dataset.title || '';
            price = Number(option.dataset.price || 0);
          }
        } else if (fixed) {
          variantId = Number(fixed.value);
          price = Number(fixed.dataset.price || 0);
        }
        items.push({
          handle: item.dataset.handle,
          title: item.dataset.title,
          covers: this.coversFor(item),
          variantId,
          variantTitle,
          price,
        });
      });

      // Keep one line per product (a product can appear in more than one group).
      const unique = [];
      const seen = new Set();
      items.forEach((item) => {
        if (seen.has(item.handle)) return;
        seen.add(item.handle);
        unique.push(item);
      });

      const coveredBy = {};
      unique.forEach((item) => {
        item.covers.forEach((handle) => {
          coveredBy[handle] = item.title;
        });
      });
      unique.forEach((item) => {
        item.coveredBy = item.covers.length === 0 ? coveredBy[item.handle] || null : null;
      });
      return unique;
    }

    coversFor(item) {
      if (item.dataset.covers) return item.dataset.covers.split(',').filter(Boolean);
      const handles = [];
      item.querySelectorAll('.routine-curated__components a[href*="/products/"]').forEach((link) => {
        const path = new URL(link.href, window.location.origin).pathname;
        const handle = path.split('/products/')[1];
        if (handle) handles.push(handle.split('/')[0]);
      });
      return handles;
    }

    markCovered() {
      const selection = this.getSelection();
      const coveredAll = {};
      selection.forEach((item) => item.covers.forEach((handle) => (coveredAll[handle] = item.title)));
      this.form.querySelectorAll('.routine-curated__item[data-handle]').forEach((item) => {
        const setTitle = item.dataset.covers ? null : coveredAll[item.dataset.handle];
        let note = item.querySelector('.routine-curated__covered-note');
        if (setTitle) {
          item.classList.add('is-covered');
          if (!note) {
            note = document.createElement('p');
            note.className = 'routine-curated__covered-note';
            item.appendChild(note);
          }
          note.textContent = this.dataset.labelCovered.replace('[set]', setTitle);
        } else {
          item.classList.remove('is-covered');
          if (note) note.remove();
        }
      });
    }

    openReview() {
      this.review.hidden = false;
      this.reviewButton.setAttribute('aria-expanded', 'true');
      this.renderReview();
      this.review.focus();
    }

    renderReview() {
      const selection = this.getSelection();
      this.reviewList.innerHTML = '';
      this.message.textContent = '';
      let total = 0;
      const missing = [];

      selection.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'routine-curated__review-item';
        const name = document.createElement('span');
        name.textContent = item.variantTitle ? `${item.title} (${item.variantTitle})` : item.title;
        li.appendChild(name);
        const detail = document.createElement('span');
        if (item.coveredBy) {
          li.classList.add('is-covered');
          detail.textContent = this.dataset.labelCovered.replace('[set]', item.coveredBy);
        } else if (!item.variantId) {
          missing.push(item.title);
          detail.textContent = this.dataset.labelChoose.replace('[product]', item.title);
          li.classList.add('needs-choice');
        } else {
          detail.textContent = this.formatMoney(item.price);
          total += item.price;
        }
        li.appendChild(detail);
        this.reviewList.appendChild(li);
      });

      const toAdd = selection.filter((item) => !item.coveredBy && item.variantId);
      this.totalValue.textContent = this.formatMoney(total);
      this.addButton.disabled = toAdd.length === 0 || missing.length > 0;
      if (selection.length === 0) this.message.textContent = this.dataset.labelNothing;
      return { selection, toAdd };
    }

    async addToBag() {
      if (this.busy) return;
      const { toAdd } = this.renderReview();
      if (!toAdd.length) return;

      this.busy = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.textContent = this.dataset.labelAdding;

      const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map((s) => s.id) : [];
      const failed = [];
      let sectionsPayload = null;

      const post = (items, withSections) =>
        fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(
            withSections && sections.length
              ? { items, sections: sections.join(','), sections_url: window.location.pathname }
              : { items }
          ),
        });

      try {
        const response = await post(
          toAdd.map((item) => ({ id: item.variantId, quantity: 1 })),
          true
        );
        if (response.ok) {
          const json = await response.json();
          sectionsPayload = json.sections || null;
        } else {
          for (const item of toAdd) {
            const single = await post([{ id: item.variantId, quantity: 1 }], false);
            if (!single.ok) {
              let reason = '';
              try {
                const err = await single.json();
                reason = err.description || err.message || '';
              } catch (e) {
                /* ignore */
              }
              failed.push(`${item.title}${reason ? ` (${reason})` : ''}`);
            }
          }
        }
      } catch (e) {
        failed.push(...toAdd.map((item) => item.title));
      }

      if (!sectionsPayload && sections.length) {
        try {
          const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
          sectionsPayload = await response.json();
        } catch (e) {
          sectionsPayload = null;
        }
      }

      this.busy = false;
      this.addButton.removeAttribute('aria-busy');
      this.addButton.textContent = this.dataset.labelAdd;
      this.addButton.disabled = false;

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'routine-curated' });
      }

      const added = toAdd.length - failed.length;
      this.message.textContent = failed.length
        ? `${this.dataset.labelFailed} ${failed.join(', ')}`
        : this.dataset.labelAdded;

      if (added > 0) {
        if (drawer && sectionsPayload) {
          drawer.classList.remove('is-empty');
          drawer.renderContents({ sections: sectionsPayload });
        } else if (!drawer) {
          window.location.href = this.dataset.cartUrl;
        }
      }
    }
  }

  customElements.define('routine-curated', RoutineCurated);
}
